/* Web account login gate (hosted mode) — M6.

   Gates the app behind a session ONLY in hosted mode; local/desktop runs init() directly. The
   login / connection-picker / add-connection overlays mirror the mobile app's design
   (cpqpilot-mobile): light card on a glow background, Inter + Plus Jakarta Sans, #F6F8FC fields,
   the brand spark, and the gradient pill button. Access token lives in memory only; the 30-day
   refresh token rides in a Secure HttpOnly cookie. This module talks to /api/auth/* with raw fetch
   and never imports api.js (api.js imports from here — one-directional). */

import { setActiveSession } from './state.js';
import { pickBootConversation } from './boot-binding.js';
import { openRemoveMenu, confirmRemoveConnection } from './connection-delete.js';
import { refreshDelayMs, shouldRetryRefresh } from './auth-refresh-policy.js';

let _accessToken = null; // in-memory only
let _refreshInFlight = null; // single-flight guard for concurrent 401s
let _refreshTimer = null; // proactive pre-expiry refresh (see _scheduleRefresh)

let _authMode = null; // "hosted" | "local"
let _googleClientId = null; // reserved: web Google sign-in (follow-up)
let _connectionId = null;
let _conversationId = null;
let _isAdmin = false; // set from /api/me in hosted mode; always false in local mode (no admin concept)
// M10 T3: cached identity fields for the v2 sidebar account card -- all set from the
// SAME /api/me payload that already populates _isAdmin above (see _applyMe() below),
// never fetched separately. Null in local mode (no /api/me call ever happens there)
// and before the hosted /api/me call resolves.
let _userEmail = null; // d.email
let _userPlan = null; // d.entitlements.display_name, e.g. "Free"/"Pro"/"Max"
let _userUsage = null; // d.usage -- {period_used_usd, cap_usd}, real hosted billing data
let _userFeatures = []; // entitlements.features from /api/me -- [] in local mode and before it resolves (fail-closed)

// Refresh BEFORE the token dies instead of waiting for a 401. A long agent run used to
// outlive its token: calls started 401ing and the stream died mid-flight, and because
// several call sites bypass the retrying wrapper the tab often only recovered on reload.
function _scheduleRefresh() {
  if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
  const delay = refreshDelayMs(_accessToken, Date.now());
  if (delay == null) return;            // unreadable token: reactive refresh still covers us
  _refreshTimer = setTimeout(() => { _refreshTimer = null; refreshAccessToken(); }, delay);
}

/** Record a freshly minted access token and arm the next proactive refresh. */
function _setAccessToken(token) {
  _accessToken = token || null;
  if (_accessToken) _scheduleRefresh();
  else if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
}

// A tab that was backgrounded past its refresh point wakes with a dead token (timers are
// throttled or coalesced while hidden), so re-check on the way back in.
if (typeof document !== "undefined" && document.addEventListener) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible" || !_accessToken) return;
    if (refreshDelayMs(_accessToken, Date.now()) === 0) refreshAccessToken();
    else _scheduleRefresh();
  });
}

export function getAccessToken() { return _accessToken; }
export function getAuthMode() { return _authMode; }
export function authHeader() { return _accessToken ? { Authorization: `Bearer ${_accessToken}` } : {}; }
export function getActiveConnection() { return _connectionId; }
export function getActiveConversation() { return _conversationId; }
export function setActiveConversation(coid) { _conversationId = coid; }
/** True when the current hosted session's /api/me confirmed is_admin: true. Always false in local mode. */
export function isAdmin() { return _isAdmin; }
/** Hosted-mode account email for the v2 sidebar card (M10 T3). Null in local mode. */
export function getUserEmail() { return _userEmail; }
/** Hosted-mode plan display name (entitlements.display_name) for the v2 sidebar card. Null in local mode. */
export function getUserPlan() { return _userPlan; }
/** Hosted-mode billing usage ({period_used_usd, cap_usd}) for the v2 sidebar Usage row. Null in local mode. */
export function getUserUsage() { return _userUsage; }
/** Hosted-mode entitlement feature flags (e.g. "promotion") from /api/me. [] in local mode. */
export function getUserFeatures() { return _userFeatures; }
/**
 * M12 Wave 1 T8 -- update the cached usage snapshot after a fresh
 * GET /api/usage (api.usage()) resolves, e.g. after an agent run completes
 * (shell-v2.js's refreshUsageRow()). Re-shapes /api/usage's own field names
 * (used_usd/allowance_usd) into the {period_used_usd, cap_usd} pair
 * getUserUsage()'s one existing consumer (shell-v2.js's _usageRowHtml())
 * already expects, so that render path stays unchanged.
 */
export function setUserUsage(usedUsd, capUsd) {
  _userUsage = { period_used_usd: usedUsd, cap_usd: capUsd };
}

// Shared /api/me field extraction -- both call sites below (_loadMe() and boot()'s
// silent-resume path) fetch the identical payload shape; this is the single place
// that reads it into module state so the two never drift apart.
function _applyMe(d) {
  _isAdmin = !!d.is_admin;
  _userEmail = d.email || null;
  _userPlan = (d.entitlements && d.entitlements.display_name) || null;
  _userUsage = d.usage || null;
  _userFeatures = (d.entitlements && d.entitlements.features) || [];
}

const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
const val = (id) => (document.getElementById(id)?.value || "").trim();
const host = (u) => { try { return new URL(u).host; } catch { return ""; } };

// Brand spark (gradient ✦) + tile icons — mirror the mobile SparkMark + Lucide glyphs.
const SPARK = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><defs><linearGradient id="cpqAuthSg" x1="2" y1="12" x2="22" y2="12" gradientUnits="userSpaceOnUse"><stop stop-color="#4285F4"/><stop offset=".5" stop-color="#9B72CB"/><stop offset="1" stop-color="#D96570"/></linearGradient></defs><path d="M12 2c.5 4.8 2.2 6.5 7 7-4.8.5-6.5 2.2-7 7-.5-4.8-2.2-6.5-7-7 4.8-.5 6.5-2.2 7-7Z" fill="url(#cpqAuthSg)"/></svg>`;
const BOLT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>`;
const CHEVRON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>`;
// Kebab (⋮) — a deliberate one-level-in affordance so a delete is never a stray tile click.
const KEBAB = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;

function _injectStyleOnce() {
  if (document.getElementById("cpqAuthStyle")) return;
  const style = document.createElement("style");
  style.id = "cpqAuthStyle";
  style.textContent = `
    #cpqAuthRoot{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;
      padding:24px;overflow:auto;color:#1F1F1F;background:#fff;
      background-image:radial-gradient(120% 120% at 56% -64%,rgba(155,114,203,.20),transparent 60%),
        radial-gradient(120% 120% at -64% -76%,rgba(66,133,244,.16),transparent 60%);
      font-family:'Inter',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;}
    #cpqAuthRoot *{box-sizing:border-box;}
    .cpqAuth-card{width:100%;max-width:420px;background:#fff;border-radius:24px;padding:32px 28px 28px;
      box-shadow:0 8px 40px rgba(0,0,0,.08);}
    .cpqAuth-brand{display:flex;align-items:center;gap:8px;margin-bottom:24px;}
    .cpqAuth-spark{width:20px;height:20px;display:inline-flex;animation:cpqSpark 3.2s ease-in-out infinite;}
    .cpqAuth-brand b{font-weight:600;font-size:15px;color:#1F1F1F;}
    @keyframes cpqSpark{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.08);opacity:.85}}
    @media (prefers-reduced-motion: reduce){.cpqAuth-spark{animation:none}}
    .cpqAuth-card h1{font-family:'Plus Jakarta Sans','Inter',sans-serif;font-size:28px;font-weight:800;
      letter-spacing:-.5px;margin:0 0 6px;color:#1F1F1F;}
    .cpqAuth-sub{margin:0 0 28px;font-size:14px;color:#5F6368;}
    .cpqAuth-field{display:flex;flex-direction:column;margin-bottom:16px;}
    .cpqAuth-field label{font-size:12px;font-weight:500;color:#5F6368;margin-bottom:6px;}
    .cpqAuth-field input,.cpqAuth-field select{background:#F6F8FC;border:1px solid #E4E7EC;border-radius:16px;
      color:#1F1F1F;padding:14px 16px;font:inherit;font-size:14px;outline:none;min-height:48px;appearance:none;}
    .cpqAuth-field input:focus,.cpqAuth-field select:focus{border:1.5px solid #1A73E8;padding:13.5px 15.5px;}
    .cpqAuth-field input::placeholder{color:#9aa0a6;}
    .cpqAuth-seg{display:flex;gap:4px;background:#F6F8FC;border:1px solid #E4E7EC;border-radius:16px;padding:4px;}
    .cpqAuth-seg button{flex:1;min-height:38px;border:0;border-radius:12px;background:transparent;color:#5F6368;
      font:inherit;font-size:13px;font-weight:500;cursor:pointer;}
    .cpqAuth-seg button.is-active{background:#fff;color:#1A73E8;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,.08);}
    .cpqAuth-btn{width:100%;min-height:48px;border:0;border-radius:999px;cursor:pointer;margin-top:8px;
      background:linear-gradient(90deg,#1A73E8 0%,#7B5CF0 55%,#C84D8F 100%);color:#fff;
      font-family:'Plus Jakarta Sans','Inter',sans-serif;font-weight:600;font-size:14px;letter-spacing:.1px;
      padding:12px 24px;box-shadow:0 8px 20px -8px rgba(123,92,240,.6);}
    .cpqAuth-btn:disabled{background:#CDD1D8;box-shadow:none;cursor:default;}
    .cpqAuth-ghost{width:100%;min-height:48px;border:1.5px solid #E4E7EC;background:transparent;border-radius:999px;
      padding:12px 24px;font:inherit;font-size:14px;font-weight:500;color:#1F1F1F;cursor:pointer;margin-top:8px;}
    .cpqAuth-alt{margin:16px 0 0;font-size:14px;color:#5F6368;text-align:center;}
    .cpqAuth-alt button{background:none;border:0;color:#1559C0;font-weight:600;cursor:pointer;font:inherit;padding:0;}
    .cpqAuth-error{margin:0 0 14px;font-size:13px;color:#D93025;text-align:center;}
    .cpqAuth-info{margin:0 0 14px;font-size:13px;color:#1a73e8;text-align:center;}
    .cpqConn-list{display:flex;flex-direction:column;gap:10px;margin:2px 0 8px;max-height:52vh;overflow:auto;}
    .cpqConn-row{position:relative;display:flex;align-items:center;}
    .cpqConn-tile{display:flex;align-items:center;gap:16px;flex:1;min-width:0;text-align:left;cursor:pointer;
      background:#F6F8FC;border:1px solid #E4E7EC;border-radius:20px;padding:18px 56px 18px 20px;
      box-shadow:0 6px 20px rgba(0,0,0,.04),0 1px 4px rgba(0,0,0,.02);}
    .cpqConn-tile:hover{border-color:#1A73E8;}
    /* Kebab sits over the tile's right padding; revealed on row hover/focus so it's never a
       stray target, but always reachable by keyboard (focus-within) + always in the DOM. */
    .cpqConn-kebab{position:absolute;right:12px;top:50%;transform:translateY(-50%);
      width:32px;height:32px;border:0;border-radius:50%;background:transparent;color:#5F6368;
      display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;
      opacity:0;transition:opacity .12s;}
    .cpqConn-kebab svg{width:18px;height:18px;}
    .cpqConn-row:hover .cpqConn-kebab,.cpqConn-row:focus-within .cpqConn-kebab{opacity:1;}
    .cpqConn-kebab:hover{background:#EEF1FF;color:#1A73E8;}
    .cpqConn-ic{width:44px;height:44px;flex-shrink:0;border-radius:12px;background:#EEF1FF;color:#1A73E8;
      display:flex;align-items:center;justify-content:center;}
    .cpqConn-ic svg{width:22px;height:22px;}
    .cpqConn-main{flex:1;min-width:0;}
    .cpqConn-label{font-family:'Plus Jakarta Sans','Inter',sans-serif;font-size:15px;font-weight:600;color:#1F1F1F;
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .cpqConn-sub{font-size:13px;color:#5F6368;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .cpqConn-badge{flex-shrink:0;padding:3px 8px;border-radius:999px;font-size:11px;font-weight:600;}
    .cpqConn-chev{flex-shrink:0;color:#5F6368;width:20px;height:20px;display:inline-flex;}
    /* Hosted mode: hide gated single-operator chrome (tenants/sync/db/knowledge/timeline). */
    #app.cpq-hosted .nav-tenant-wrap,
    #app.cpq-hosted #syncBadges,
    #app.cpq-hosted #btnRefreshSync,
    #app.cpq-hosted #btnGlobalSearch,
    #app.cpq-hosted #sbBranch,
    #app.cpq-hosted #sbChanges,
    #app.cpq-hosted #sbSyncIndicator,
    #app.cpq-hosted .activity-bar .ab-btn[data-panel="knowledge"],
    #app.cpq-hosted .activity-bar .ab-btn[data-panel="sync-status"],
    #app.cpq-hosted .activity-bar .ab-btn[data-panel="timeline"]{ display:none !important; }
    /* Hosted pipeline is a read-only fields-tree: hide the quote-events scope tabs
       (their /api/execution-order is single-operator and 404s in hosted). */
    #app.cpq-hosted .pipeline-scope{ display:none !important; }
  `;
  document.head.appendChild(style);
}

function _overlayRoot() {
  let root = document.getElementById("cpqAuthRoot");
  if (!root) {
    root = document.createElement("div");
    root.id = "cpqAuthRoot";
    document.body.appendChild(root);
  }
  return root;
}

function _removeOverlay() {
  document.getElementById("cpqAuthRoot")?.remove();
}

async function _probeMode() {
  try {
    const r = await fetch("/health", { headers: { Accept: "application/json" } });
    if (r.ok) {
      const d = await r.json();
      _authMode = d.auth_mode === "hosted" ? "hosted" : "local";
      _googleClientId = d.google_client_id || null;
      return _authMode;
    }
  } catch {
    /* fall through */
  }
  _authMode = "local"; // fail open to local/desktop behavior
  return _authMode;
}

/** Single-flight silent refresh via the httpOnly cookie. Returns true if an access token was minted. */
export function refreshAccessToken() {
  if (_refreshInFlight) return _refreshInFlight;
  const attemptRefresh = async (attempt = 0) => {
    try {
      const r = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: "{}",
      });
      if (!r.ok) {
        // Single-use refresh tokens make concurrent tabs race: the loser 401s even though
        // the winner just installed a valid cookie. Retry once with the CURRENT cookie
        // before declaring the session dead -- otherwise this tab shows "session expired"
        // while a working credential sits in the jar, and only a reload recovers it.
        if (shouldRetryRefresh(attempt)) return attemptRefresh(attempt + 1);
        _setAccessToken(null);
        return false;
      }
      const d = await r.json();
      _setAccessToken(d.access_token || null);
      return !!_accessToken;
    } catch {
      if (shouldRetryRefresh(attempt)) return attemptRefresh(attempt + 1);
      _setAccessToken(null);
      return false;
    }
  };
  // Release the single-flight guard only once the WHOLE chain (including the retry)
  // has settled. A `finally` inside attemptRefresh would fire the moment the retry is
  // returned, re-opening the guard mid-flight and letting a concurrent 401 start a
  // third refresh -- which is exactly the rotation race this retry exists to absorb.
  _refreshInFlight = attemptRefresh(0).finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

// auth.js cannot import api.js (one-directional dependency), so it carries its own
// bearer + 401-refresh-retry wrapper. Without it these calls -- /api/me and the
// connection lookups a returning user hits FIRST -- fail hard on an expired token and
// drop the user to the login overlay while a valid refresh cookie sits in the jar.
async function _authedFetch(url, opts = {}, _retried = false) {
  const merged = { ...opts, headers: { ...(opts && opts.headers), ...authHeader() } };
  const r = await fetch(url, merged);
  if (r.status === 401 && !_retried && _authMode === "hosted") {
    if (await refreshAccessToken()) return _authedFetch(url, opts, true);
  }
  return r;
}

async function _post(path, body) {
  const r = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(data.detail || `HTTP ${r.status}`);
    e.status = r.status;
    e.code = data.code; // machine-readable reason (e.g. "email_unverified", "invalid_code")
    throw e;
  }
  return data;
}

/** Fetch /api/me with the current bearer token and populate _isAdmin. Best-effort — never throws. */
async function _loadMe() {
  try {
    const r = await _authedFetch("/api/me", { headers: { ...authHeader(), Accept: "application/json" } });
    if (r.ok) {
      const d = await r.json();
      _applyMe(d);
    }
  } catch {
    /* best-effort; _isAdmin stays false (fail-closed) */
  }
}

export async function logout() {
  try {
    await _post("/api/auth/logout", {});
  } catch {
    /* best effort — clear locally regardless */
  }
  _accessToken = null;
  _isAdmin = false; // reset on logout
  _userEmail = null;
  _userPlan = null;
  _userUsage = null;
  _userFeatures = [];
  location.reload();
}

function _wireLogout() {
  const avatar = document.getElementById("navAvatar");
  if (!avatar || avatar._cpqLogoutWired) return;
  avatar._cpqLogoutWired = true;
  avatar.style.cursor = "pointer";
  avatar.title = "Log out";
  avatar.addEventListener("click", () => {
    if (confirm("Log out of AgentCPQ?")) logout();
  });
}

async function _enterApp(initFn) {
  window.__cpqBooted = true; // signal (used by E2E): the app shell has been entered post-auth
  document.documentElement.classList.remove("cpq-booting"); // reveal the app shell (was hidden to avoid a flash behind the gate)
  _injectStyleOnce();
  document.getElementById("app")?.classList.add("cpq-hosted"); // _enterApp runs only in hosted mode
  _wireLogout();
  try {
    await initFn();
  } catch (e) {
    // Hosted IDE surfaces (file tree, sync, …) land in a later phase, so their calls 401/404 until
    // then; a partial init must not tear down the shell we just revealed.
    console.warn("init after auth completed with errors (expected pre-Phase-4):", e);
  }
}

// ── Connection selection ─────────────────────────────────────────────────────

export async function useConnection(connId) {
  // Bind the UI session to the conversation the user would expect after a reload: a
  // still-running one first (so a live run is re-attached before its resume grace
  // expires), else the most recently active, else mint a fresh one. The pure pick
  // lives in boot-binding.js.
  _connectionId = connId;
  const cv = await _authedFetch(`/api/connections/${encodeURIComponent(connId)}/conversations`, {
    headers: { ...authHeader(), Accept: "application/json" },
  }).then((r) => (r.ok ? r.json() : {}));
  const list = (cv && cv.conversations) || [];
  const picked = pickBootConversation(list);
  if (picked) {
    _conversationId = picked.id;
  } else {
    const made = await _authedFetch(`/api/connections/${encodeURIComponent(connId)}/conversations`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeader() }, body: "{}",
    }).then((r) => r.json());
    _conversationId = made.id;
  }
  setActiveSession(_conversationId);
}

/** After login/refresh: fetch the user's connections and show the picker (or the add-connection
 *  form if they have none). Mirrors the mobile ConnectionsScreen home. */
async function _chooseConnection(initFn) {
  let conns = [];
  try {
    const d = await _authedFetch("/api/connections", {
      headers: { ...authHeader(), Accept: "application/json" },
    }).then((r) => (r.ok ? r.json() : {}));
    conns = (d && d.connections) || [];
  } catch {
    /* treat as none */
  }
  if (!conns.length) _renderAddConnection(initFn, "", false);
  else _renderConnectionPicker(initFn, conns);
}

function _envBadge(role) {
  const r = String(role).toLowerCase();
  const c = r === "prod" ? "background:#E8F0FE;color:#1A73E8"
    : r === "test" ? "background:#F3EEFF;color:#9B72CB"
    : "background:#E4E7EC;color:#5F6368";
  return `<span class="cpqConn-badge" style="${c}">${esc(r)}</span>`;
}

function _connTileHtml(c) {
  const sub = c.client_label || host(c.sap_base_url) || "";
  // A <button> can't nest a <button>, so the kebab is a SIBLING of the select tile (both in
  // .cpqConn-row). The kebab opens a one-item "Remove connection" menu — delete lives a deliberate
  // level in, never a stray tile click. data-id via esc() (attribute-position), uniform with every
  // other interpolation in this overlay.
  return `<div class="cpqConn-row">
    <button class="cpqConn-tile" data-id="${esc(c.id)}" type="button">
      <span class="cpqConn-ic">${BOLT}</span>
      <span class="cpqConn-main">
        <span class="cpqConn-label">${esc(c.label || "Connection")}</span>
        ${sub ? `<span class="cpqConn-sub">${esc(sub)}</span>` : ""}
      </span>
      ${c.env_role ? _envBadge(c.env_role) : ""}
      <span class="cpqConn-chev">${CHEVRON}</span>
    </button>
    <button class="cpqConn-kebab" data-id="${esc(c.id)}" type="button"
      aria-label="Connection actions" aria-haspopup="menu" title="More actions">${KEBAB}</button>
  </div>`;
}

function _renderConnectionPicker(initFn, conns) {
  _injectStyleOnce();
  const root = _overlayRoot();
  root.innerHTML = `
    <div class="cpqAuth-card">
      <div class="cpqAuth-brand"><span class="cpqAuth-spark">${SPARK}</span><b>AgentCPQ</b></div>
      <h1>Choose a connection</h1>
      <p class="cpqAuth-sub">Pick the SAP CPQ tenant to work in.</p>
      <div class="cpqConn-list" id="cpqConnList">${conns.map(_connTileHtml).join("")}</div>
      <button class="cpqAuth-ghost" id="cpqConnAdd" type="button">+ Add a connection</button>
      <p class="cpqAuth-alt"><button type="button" id="cpqPickLogout">Log out</button></p>
    </div>`;
  root.querySelectorAll(".cpqConn-tile").forEach((el) =>
    el.addEventListener("click", () => _selectConnection(initFn, el.dataset.id)));
  root.querySelectorAll(".cpqConn-kebab").forEach((el) =>
    el.addEventListener("click", (e) => {
      e.stopPropagation(); // don't select the tile behind it, and don't hit the menu's doc-close
      const c = conns.find((x) => x.id === el.dataset.id);
      if (c) openRemoveMenu(el, () => _confirmAndRemove(initFn, c));
    }));
  root.querySelector("#cpqConnAdd").onclick = () => _renderAddConnection(initFn, "", true);
  root.querySelector("#cpqPickLogout").onclick = () => logout();
}

/** Raw-fetch DELETE of a connection. auth.js is deliberately standalone (never imports api.js —
 *  api.js imports THIS module), so it uses the same authHeader()+fetch idiom as useConnection()
 *  and _submitConnection() rather than api.deleteConnection(). For a PROD connection the server
 *  enforces a typed-confirm gate, so a non-null `typedConfirm` (the label the user typed) is sent
 *  as `typed_confirm` in the DELETE body; non-prod deletes send no body. */
async function _deleteConnection(connId, typedConfirm = null) {
  const r = await _authedFetch(`/api/connections/${encodeURIComponent(connId)}`, {
    method: "DELETE",
    headers: { ...authHeader(), ...(typedConfirm ? { "Content-Type": "application/json" } : {}) },
    credentials: "same-origin",
    body: typedConfirm ? JSON.stringify({ typed_confirm: typedConfirm }) : undefined,
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `Could not remove the connection (HTTP ${r.status}).`);
  }
  return r.json().catch(() => ({}));
}

/** Kebab → "Remove connection": show the shared hardened confirm (naming label/host/env, prod
 *  typed-confirm), DELETE, then re-route through _chooseConnection() — which re-fetches and shows
 *  the picker, or the friendly add-connection empty state when that was the last connection. On
 *  failure the picker is left intact and an inline error is shown. No active connection exists at
 *  the picker (pre-app), so no switch-out routing is needed here. */
const _deletingConns = new Set(); // in-flight ids — a second trigger can't fire a duplicate DELETE
async function _confirmAndRemove(initFn, c) {
  const res = await confirmRemoveConnection({
    label: c.label, host: host(c.sap_base_url), envRole: c.env_role, isActive: false,
  });
  if (!res || _deletingConns.has(c.id)) return;
  _deletingConns.add(c.id);
  let deleted = false;
  try {
    await _deleteConnection(c.id, res.typedConfirm); // prod sends typed_confirm; non-prod sends null
    deleted = true;
  } catch (err) {
    _showPickerError(err.message || "Could not remove the connection."); // picker unchanged — row stays
  } finally {
    _deletingConns.delete(c.id);
  }
  if (deleted) await _chooseConnection(initFn);
}

/** Surface an error banner inside the still-showing picker card (a failed delete leaves the row). */
function _showPickerError(msg) {
  const card = document.getElementById("cpqAuthRoot")?.querySelector(".cpqAuth-card");
  if (!card) return;
  let b = card.querySelector(".cpqAuth-error");
  if (!b) {
    b = document.createElement("div");
    b.className = "cpqAuth-error";
    b.setAttribute("role", "alert");
    card.querySelector("h1")?.after(b);
  }
  b.textContent = msg;
}

async function _selectConnection(initFn, connId) {
  try {
    await useConnection(connId);
  } catch {
    /* enter anyway; the run socket will retry once the conversation exists */
  }
  _removeOverlay();
  await _enterApp(initFn);
}

function _renderAddConnection(initFn, err = "", canBack = false, authMode = "password", onCreated = null) {
  _injectStyleOnce();
  const root = _overlayRoot();
  // Auth-method toggle mirrors the mobile SegmentedButton (Password | Client credentials). Both
  // credential groups stay in the DOM (inactive one hidden) so typed values survive switching.
  const cc = authMode === "client_credentials";
  root.innerHTML = `
    <form class="cpqAuth-card" id="cpqConnForm" novalidate>
      <div class="cpqAuth-brand"><span class="cpqAuth-spark">${SPARK}</span><b>AgentCPQ</b></div>
      <h1>Add a connection</h1>
      <p class="cpqAuth-sub">Connect a SAP CPQ tenant for the agent to work in.</p>
      ${err ? `<div class="cpqAuth-error" role="alert">${esc(err)}</div>` : ""}
      <div class="cpqAuth-field"><label for="cpqConnLabel">Label</label>
        <input id="cpqConnLabel" type="text" placeholder="Production" required /></div>
      <div class="cpqAuth-field"><label for="cpqConnUrl">SAP CPQ base URL</label>
        <input id="cpqConnUrl" type="url" placeholder="https://tenant.cpq.cloud.sap/Login.aspx" required /></div>
      <div class="cpqAuth-field"><label>Authentication</label>
        <div class="cpqAuth-seg" id="cpqConnAuthMode" role="group" aria-label="Authentication method">
          <button type="button" data-mode="password" class="${cc ? "" : "is-active"}" aria-pressed="${!cc}">Password</button>
          <button type="button" data-mode="client_credentials" class="${cc ? "is-active" : ""}" aria-pressed="${cc}">Client credentials</button>
        </div></div>
      <div id="cpqConnPassFields"${cc ? ' style="display:none"' : ""}>
        <div class="cpqAuth-field"><label for="cpqConnUser">Username</label>
          <input id="cpqConnUser" type="text" autocomplete="off" /></div>
        <div class="cpqAuth-field"><label for="cpqConnPass">Password</label>
          <input id="cpqConnPass" type="password" autocomplete="off" /></div>
      </div>
      <div id="cpqConnCcFields"${cc ? "" : ' style="display:none"'}>
        <div class="cpqAuth-field"><label for="cpqConnClientId">Client ID</label>
          <input id="cpqConnClientId" type="text" autocomplete="off" /></div>
        <div class="cpqAuth-field"><label for="cpqConnClientSecret">Client secret</label>
          <input id="cpqConnClientSecret" type="password" autocomplete="off" /></div>
      </div>
      <div class="cpqAuth-field"><label for="cpqConnClient">Customer / account (optional)</label>
        <input id="cpqConnClient" type="text" placeholder="Coca-Cola" /></div>
      <div class="cpqAuth-field"><label for="cpqConnEnv">Environment (optional)</label>
        <select id="cpqConnEnv"><option value="">None</option><option value="dev">dev</option><option value="test">test</option><option value="prod">prod</option></select></div>
      <div class="cpqAuth-field"><label for="cpqConnFamily">Family (optional)</label>
        <input id="cpqConnFamily" type="text" placeholder="bosch-bucat" /></div>
      <button class="cpqAuth-btn" type="submit" id="cpqConnSubmit">Create connection</button>
      <p class="cpqAuth-alt"><button type="button" id="cpqConnBack">${canBack ? "Back" : "Log out"}</button></p>
    </form>`;
  // With onCreated set (mid-session add from the in-app dropdown), "Back" just dismisses the
  // overlay -- no logout, no return to the boot picker (the app stays on the current connection).
  root.querySelector("#cpqConnBack").onclick = () =>
    (onCreated ? _removeOverlay() : (canBack ? _chooseConnection(initFn) : logout()));
  root.querySelectorAll("#cpqConnAuthMode button").forEach((b) =>
    b.addEventListener("click", () => {
      root.querySelectorAll("#cpqConnAuthMode button").forEach((x) => {
        const on = x === b;
        x.classList.toggle("is-active", on);
        x.setAttribute("aria-pressed", String(on));
      });
      const showCc = b.dataset.mode === "client_credentials";
      document.getElementById("cpqConnPassFields").style.display = showCc ? "none" : "";
      document.getElementById("cpqConnCcFields").style.display = showCc ? "" : "none";
    }));
  root.querySelector("#cpqConnForm").onsubmit = (e) => {
    e.preventDefault();
    _submitConnection(initFn, canBack, onCreated);
  };
  root.querySelector("#cpqConnLabel").focus();
}

async function _submitConnection(initFn, canBack, onCreated = null) {
  const auth_mode = document.querySelector("#cpqConnAuthMode .is-active")?.dataset.mode || "password";
  const label = val("cpqConnLabel");
  const sap_base_url = val("cpqConnUrl");
  const client_label = val("cpqConnClient") || undefined;
  const env_role = val("cpqConnEnv") || undefined;
  // Family groups a connection into a dev->test->prod promotion pipeline (the Promotion panel
  // promotes a release across same-family connections up the env rung).
  const family = val("cpqConnFamily") || undefined;
  const btn = document.getElementById("cpqConnSubmit");
  if (!label || !sap_base_url) {
    return _renderAddConnection(initFn, "Fill in the required fields.", canBack, auth_mode, onCreated);
  }
  // Only the active mode's credential pair is transmitted (mirrors the mobile client).
  const body = { label, sap_base_url, auth_mode, client_label, env_role, family };
  if (auth_mode === "client_credentials") {
    body.client_id = val("cpqConnClientId");
    body.client_secret = document.getElementById("cpqConnClientSecret").value;
    if (!body.client_id || !body.client_secret) {
      return _renderAddConnection(initFn, "Enter the client ID and client secret.", canBack, auth_mode, onCreated);
    }
  } else {
    body.username = val("cpqConnUser");
    body.password = document.getElementById("cpqConnPass").value;
    if (!body.username || !body.password) {
      return _renderAddConnection(initFn, "Enter the username and password.", canBack, auth_mode, onCreated);
    }
  }
  btn.disabled = true;
  btn.textContent = "Creating…";
  try {
    const r = await _authedFetch("/api/connections", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || `Could not add the connection (HTTP ${r.status}).`);
    }
    const c = await r.json();
    if (onCreated) {
      // Mid-session add (from the in-app dropdown): hand the new connection id back to the
      // caller (the in-place switch) instead of re-running the full post-auth app entry.
      _removeOverlay();
      await onCreated(c.id);
    } else {
      await useConnection(c.id);
      _removeOverlay();
      await _enterApp(initFn); // straight into the chat on the new connection
    }
  } catch (e) {
    _renderAddConnection(initFn, e.message || "Could not add the connection.", canBack, auth_mode, onCreated);
  }
}

/**
 * Open the add-connection overlay mid-session (from the in-app connection dropdown),
 * without leaving the current connection. `onCreated(newConnId)` runs after a successful
 * create (the caller performs the in-place switch); "Back" just dismisses the overlay.
 */
export function openAddConnection(onCreated) {
  _injectStyleOnce();
  _renderAddConnection(null, "", true, "password", onCreated);
}

// ── Login ────────────────────────────────────────────────────────────────────

function _renderLogin(initFn, mode = "login", err = "") {
  _injectStyleOnce();
  const root = _overlayRoot();
  const signup = mode === "signup";
  root.innerHTML = `
    <form class="cpqAuth-card" id="cpqAuthForm" novalidate>
      <div class="cpqAuth-brand"><span class="cpqAuth-spark">${SPARK}</span><b>AgentCPQ</b></div>
      <h1>${signup ? "Create your account" : "Welcome back"}</h1>
      <p class="cpqAuth-sub">${signup ? "Start automating SAP CPQ." : "Sign in to continue."}</p>
      ${err ? `<div class="cpqAuth-error" role="alert">${esc(err)}</div>` : ""}
      <div class="cpqAuth-field"><label for="cpqAuthEmail">Email</label>
        <input id="cpqAuthEmail" type="email" autocomplete="email" placeholder="you@example.com" required /></div>
      <div class="cpqAuth-field"><label for="cpqAuthPassword">Password</label>
        <input id="cpqAuthPassword" type="password" autocomplete="${signup ? "new-password" : "current-password"}" placeholder="••••••••" required /></div>
      <button class="cpqAuth-btn" type="submit" id="cpqAuthSubmit">${signup ? "Create account" : "Log in"}</button>
      <p class="cpqAuth-alt">${signup ? "Already have an account?" : "New to AgentCPQ?"}
        <button type="button" id="cpqAuthToggle">${signup ? "Log in" : "Create one"}</button></p>
    </form>`;
  root.querySelector("#cpqAuthToggle").onclick = () => _renderLogin(initFn, signup ? "login" : "signup");
  root.querySelector("#cpqAuthForm").onsubmit = (e) => {
    e.preventDefault();
    _submit(initFn, mode);
  };
  root.querySelector("#cpqAuthEmail").focus();
}

async function _submit(initFn, mode) {
  const email = val("cpqAuthEmail");
  const password = document.getElementById("cpqAuthPassword").value;
  const btn = document.getElementById("cpqAuthSubmit");
  if (!email || !password) return _renderLogin(initFn, mode, "Enter your email and password.");
  btn.disabled = true;
  btn.textContent = mode === "signup" ? "Creating…" : "Signing in…";
  try {
    if (mode === "signup") {
      const reg = await _post("/api/auth/register", { email, password });
      // Verification on → go enter the code; verification off (no provider) → fall through to login.
      if (reg && reg.verification_required) return _renderVerifyCode(initFn, email);
    }
    const d = await _post("/api/auth/login", { email, password });
    _setAccessToken(d.access_token || null);
    await _loadMe(); // populate _isAdmin before entering the app
    await _chooseConnection(initFn); // → connection picker or add-connection (same overlay)
  } catch (e) {
    if (e.code === "email_unverified") {
      // Logging in with an unverified account — send a fresh code and route into the verify screen.
      try { await _post("/api/auth/resend-code", { email }); } catch { /* best-effort */ }
      return _renderVerifyCode(initFn, email, "", "Your email isn't verified yet — we sent you a code.");
    }
    _renderLogin(initFn, mode, e.message || "Something went wrong. Try again.");
  }
}

function _renderVerifyCode(initFn, email, err = "", info = "") {
  _injectStyleOnce();
  const root = _overlayRoot();
  root.innerHTML = `
    <form class="cpqAuth-card" id="cpqVerifyForm" novalidate>
      <div class="cpqAuth-brand"><span class="cpqAuth-spark">${SPARK}</span><b>AgentCPQ</b></div>
      <h1>Verify your email</h1>
      <p class="cpqAuth-sub">Enter the 6-digit code we sent to ${esc(email)}.</p>
      ${err ? `<div class="cpqAuth-error" role="alert">${esc(err)}</div>` : ""}
      ${info ? `<div class="cpqAuth-info" role="status">${esc(info)}</div>` : ""}
      <div class="cpqAuth-field"><label for="cpqVerifyCode">Verification code</label>
        <input id="cpqVerifyCode" type="text" inputmode="numeric" autocomplete="one-time-code"
               maxlength="6" pattern="[0-9]*" placeholder="123456" required /></div>
      <button class="cpqAuth-btn" type="submit" id="cpqVerifySubmit">Verify</button>
      <p class="cpqAuth-alt">Didn't get it?
        <button type="button" id="cpqVerifyResend">Resend code</button></p>
      <p class="cpqAuth-alt"><button type="button" id="cpqVerifyBack">Back to sign in</button></p>
    </form>`;
  root.querySelector("#cpqVerifyForm").onsubmit = (e) => { e.preventDefault(); _submitVerify(initFn, email); };
  root.querySelector("#cpqVerifyResend").onclick = () => _submitResend(email);
  root.querySelector("#cpqVerifyBack").onclick = () => _renderLogin(initFn, "login");
  root.querySelector("#cpqVerifyCode").focus();
}

async function _submitVerify(initFn, email) {
  const code = (document.getElementById("cpqVerifyCode").value || "").trim();
  const btn = document.getElementById("cpqVerifySubmit");
  if (!code) return _renderVerifyCode(initFn, email, "Enter the code from your email.");
  btn.disabled = true;
  btn.textContent = "Verifying…";
  try {
    const d = await _post("/api/auth/verify-email", { email, code });
    _accessToken = d.access_token || null;
    await _loadMe();
    await _chooseConnection(initFn); // same "you're in" tail as login
  } catch (e) {
    _renderVerifyCode(initFn, email, e.message || "That code didn't work. Try again.");
  }
}

async function _submitResend(email) {
  const btn = document.getElementById("cpqVerifyResend");
  if (!btn || btn.disabled) return;
  let secs = 60;
  try {
    const d = await _post("/api/auth/resend-code", { email });
    if (d && d.retry_after) secs = d.retry_after;
  } catch { /* best-effort — the button still enters cooldown */ }
  const label = btn.textContent;
  btn.disabled = true;
  const tick = () => {
    if (secs <= 0) { clearInterval(iv); btn.disabled = false; btn.textContent = label; return; }
    btn.textContent = `Resend in ${secs}s`;
    secs -= 1;
  };
  tick();
  const iv = setInterval(tick, 1000);
}

/**
 * Gate the app behind a session in hosted mode; run init() directly in local/desktop mode.
 * @param {() => (void|Promise<void>)} initFn the app's init() (builds + wires the IDE)
 */
export async function boot(initFn) {
  const mode = await _probeMode();
  if (mode !== "hosted") {
    document.documentElement.classList.remove("cpq-booting"); // local/desktop: reveal immediately
    await initFn();
    window.__cpqBooted = true;
    return;
  }
  // Hosted: try to resume a session silently via the refresh cookie, confirm it, then pick a connection.
  if (await refreshAccessToken()) {
    try {
      const r = await _authedFetch("/api/me", { headers: { ...authHeader(), Accept: "application/json" } });
      if (r.ok) {
        const d = await r.json();
        _applyMe(d); // populate admin status + identity on silent-resume path
        await _chooseConnection(initFn);
        return;
      }
    } catch {
      /* fall through to login */
    }
    _accessToken = null;
    _isAdmin = false;
    _userEmail = null;
    _userPlan = null;
    _userUsage = null;
    _userFeatures = [];
  }
  _renderLogin(initFn);
}

// Deterministic e2e hook (mirrors window.__test_workspace/__test_code/__test_templates
// elsewhere in this app): fakes a connection switch's ONE observable effect -- what
// getActiveConnection() returns -- without running the full network-bound useConnection()
// flow (there is no live hosted backend in the e2e harness). This is exactly the signal
// templates-view.js's tenant/connection-switch staleness guard on its WSC_EVENT subscriber
// keys off, so it's the seam a test needs to simulate that race deterministically.
//
// setAuthMode() is the same idea for _authMode: api.js's fileContent()/etc. route to the
// per-connection hosted URL shape (and only there does `?source=workspace|cloud` ever
// appear) only when getAuthMode() === "hosted". The e2e harness runs a plain `serve --http`
// (local/desktop mode, per its own boot probe against /health) with no live hosted backend to
// authenticate a real session against, so the Code view's Workspace/Cloud/Diff source toggle
// (code-view.js) has no way to exercise the real hosted fetch branch without this seam.
if (typeof window !== "undefined") {
  window.__test_auth = {
    setActiveConnection(id) { _connectionId = id; },
    setAuthMode(mode) { _authMode = mode; },
  };
}
